import { useState } from "react";
import { Navigation } from "../cmps/homepage/navigation";
import { FooterSection } from "../cmps/homepage/footer-section";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ChevronRight, Check, Mail, MessageSquare } from "lucide-react";

const requestTypes = [
  { id: "project", label: "Project Management", description: "Supervise construction, IT, or infrastructure projects" },
  { id: "finance", label: "Finance & Accounting", description: "Oversee financial operations and reporting" },
  { id: "marketing", label: "Marketing & Brand", description: "Manage marketing campaigns and brand initiatives" },
  { id: "operations", label: "Operations", description: "Coordinate business operations and logistics" },
  { id: "vendor", label: "Vendor Management", description: "Oversee supplier and contractor relationships" },
  { id: "other", label: "Other", description: "Different management needs" },
];

const mockProfessionals = [
  {
    id: 1,
    name: "Rachel Thompson",
    role: "Senior Project Manager",
    expertise: ["Construction", "Infrastructure", "Timeline Management"],
    rating: 4.9,
    projects: 127,
    avatar: "👩‍💼",
  },
  {
    id: 2,
    name: "David Kumar",
    role: "Operations Director",
    expertise: ["Supply Chain", "Vendor Management", "Cost Control"],
    rating: 4.8,
    projects: 94,
    avatar: "👨‍💼",
  },
  {
    id: 3,
    name: "Elena Rodriguez",
    role: "Financial Analyst",
    expertise: ["Budget Oversight", "Risk Management", "Reporting"],
    rating: 4.9,
    projects: 112,
    avatar: "👩‍💼",
  },
];

export default function QuickRequestPage() {
  const [step, setStep] = useState("form");
  const [selectedType, setSelectedType] = useState("");
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    company: "",
    description: "",
  });
  const [scanProgress, setScanProgress] = useState(0);
  const [feedbackData, setFeedbackData] = useState({
    email: "",
    feedback: "",
  });

  const handleTypeSelect = (typeId) => {
    setSelectedType(typeId);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedType && formData.fullName && formData.email && formData.description) {
      setStep("analyzing");
      setScanProgress(0);

      const interval = setInterval(() => {
        setScanProgress((prev) => {
          if (prev >= 100) {
            clearInterval(interval);
            setTimeout(() => setStep("results"), 500);
            return 100;
          }
          return prev + Math.random() * 20;
        });
      }, 300);
    }
  };

  const handleFeedbackSubmit = (e) => {
    e.preventDefault();
    setStep("form");
    setFormData({ fullName: "", email: "", company: "", description: "" });
    setSelectedType("");
    setFeedbackData({ email: "", feedback: "" });
  };

  return (
    <div className="homepage-root">
      <main className="relative min-h-screen overflow-x-hidden bg-background">
        <Navigation />

        <div className="pt-32 pb-20">
          <div className="max-w-2xl mx-auto px-6">
            {step === "form" && (
              <div className="animate-in fade-in duration-500">
                <div className="mb-12">
                  <h1 className="text-5xl md:text-6xl font-display tracking-tight mb-4">
                    Quick Request
                  </h1>
                  <p className="text-xl text-muted-foreground">
                    Tell us what you need managed. We'll match you with the right professional.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div>
                    <label className="block text-sm font-semibold mb-4">
                      What do you need management for?
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {requestTypes.map((type) => (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => handleTypeSelect(type.id)}
                          className={`p-4 rounded-lg border-2 transition-all text-left ${
                            selectedType === type.id
                              ? "border-red-900 bg-red-50 dark:bg-red-950/20"
                              : "border-foreground/10 hover:border-foreground/20 bg-background"
                          }`}
                        >
                          <div className="font-semibold text-sm">{type.label}</div>
                          <div className="text-xs text-muted-foreground mt-1">
                            {type.description}
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold mb-2">Full name</label>
                      <Input
                        type="text"
                        name="fullName"
                        placeholder="Your full name"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className="h-11"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Work email</label>
                      <Input
                        type="email"
                        name="email"
                        placeholder="you@company.com"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="h-11"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">Company</label>
                      <Input
                        type="text"
                        name="company"
                        placeholder="Your company name"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="h-11"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold mb-2">
                        Describe what needs to be managed
                      </label>
                      <Textarea
                        name="description"
                        placeholder="Tell us about the project, budget, timeline, and any specific requirements..."
                        value={formData.description}
                        onChange={handleInputChange}
                        required
                        className="min-h-32 resize-none"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={!selectedType || !formData.fullName || !formData.email || !formData.description}
                    className="w-full h-12 bg-red-900 hover:bg-red-800 text-white rounded-lg"
                  >
                    Analyze & Match
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
              </div>
            )}

            {step === "analyzing" && (
              <div className="animate-in fade-in duration-500 flex flex-col items-center justify-center py-20">
                <div className="mb-8">
                  <div className="text-6xl mb-4 text-center">📊</div>
                  <h2 className="text-3xl font-display text-center mb-4">Analyzing your request</h2>
                  <p className="text-muted-foreground text-center">
                    We're finding the best match for your project...
                  </p>
                </div>

                <div className="w-full max-w-xs mb-8">
                  <div className="relative h-64 bg-gradient-to-b from-red-900/20 to-transparent rounded-lg border border-red-900/30 overflow-hidden">
                    <div
                      className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-red-900 to-transparent animate-pulse"
                      style={{
                        top: `${scanProgress}%`,
                        transition: "top 0.1s linear",
                      }}
                    />

                    <div className="absolute inset-0 opacity-30">
                      {[...Array(16)].map((_, i) => (
                        <div
                          key={i}
                          className="absolute w-full h-px border-t border-red-900/20"
                          style={{ top: `${(i + 1) * 6.25}%` }}
                        />
                      ))}
                    </div>

                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <div className="text-4xl font-display font-bold text-red-900 mb-2">
                          {Math.round(scanProgress)}%
                        </div>
                        <div className="text-xs text-muted-foreground">Scanning professionals</div>
                      </div>
                    </div>
                  </div>
                </div>

                <p className="text-sm text-muted-foreground animate-pulse">
                  Matching your project requirements...
                </p>
              </div>
            )}

            {step === "results" && (
              <div className="animate-in fade-in duration-500">
                <div className="mb-12">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-950 flex items-center justify-center">
                      <Check className="w-6 h-6 text-green-900" />
                    </div>
                    <h2 className="text-3xl font-display">Perfect match found!</h2>
                  </div>
                  <p className="text-muted-foreground">
                    We've identified the top professionals for your request.
                  </p>
                </div>

                <div className="bg-gradient-to-br from-red-900/10 to-transparent border-2 border-red-900/30 rounded-xl p-6 mb-8">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex gap-4">
                      <div className="text-5xl">{mockProfessionals[0].avatar}</div>
                      <div>
                        <h3 className="text-xl font-semibold">{mockProfessionals[0].name}</h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          {mockProfessionals[0].role}
                        </p>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold">⭐ {mockProfessionals[0].rating}</span>
                          <span className="text-xs text-muted-foreground">
                            ({mockProfessionals[0].projects} projects)
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="inline-flex items-center rounded-full bg-green-100 dark:bg-green-950 px-3 py-1 text-xs font-semibold text-green-900">
                      ✓ Allocated
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-2">Core Expertise</p>
                    <div className="flex flex-wrap gap-2">
                      {mockProfessionals[0].expertise.map((skill) => (
                        <span
                          key={skill}
                          className="inline-flex items-center rounded-full bg-foreground/5 px-3 py-1 text-xs font-medium text-foreground"
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-8">
                  <h4 className="text-sm font-semibold mb-3">Other top matches</h4>
                  <div className="space-y-3">
                    {mockProfessionals.slice(1).map((prof) => (
                      <div
                        key={prof.id}
                        className="p-4 rounded-lg border border-foreground/10 bg-background hover:border-foreground/20 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex gap-3 items-center">
                            <div className="text-3xl">{prof.avatar}</div>
                            <div>
                              <div className="font-semibold text-sm">{prof.name}</div>
                              <div className="text-xs text-muted-foreground">
                                {prof.role}
                              </div>
                            </div>
                          </div>
                          <div className="text-sm font-semibold">⭐ {prof.rating}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-foreground/5 rounded-lg p-6 mb-8">
                  <h4 className="font-semibold mb-3">What happens next?</h4>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex gap-2">
                      <span className="text-foreground">✓</span>
                      <span>Supervisor will review your project details</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-foreground">✓</span>
                      <span>They'll reach out within 24 hours</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-foreground">✓</span>
                      <span>You'll discuss timeline, budget & expectations</span>
                    </li>
                  </ul>
                </div>

                <Button
                  onClick={() => setStep("feedback")}
                  className="w-full h-12 bg-red-900 hover:bg-red-800 text-white rounded-lg"
                >
                  Continue to Feedback
                </Button>
              </div>
            )}

            {step === "feedback" && (
              <div className="animate-in fade-in duration-500">
                <div className="mb-12">
                  <h2 className="text-3xl font-display mb-4">One more thing</h2>
                  <p className="text-muted-foreground">
                    Help us improve by sharing your thoughts about this process.
                  </p>
                </div>

                <form onSubmit={handleFeedbackSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Your email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3.5 w-5 h-5 text-muted-foreground" />
                      <Input
                        type="email"
                        name="email"
                        placeholder="you@company.com"
                        value={feedbackData.email}
                        onChange={(e) =>
                          setFeedbackData((prev) => ({
                            ...prev,
                            email: e.target.value,
                          }))
                        }
                        required
                        className="pl-10 h-11"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2">
                      Your feedback
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-3 top-3 w-5 h-5 text-muted-foreground" />
                      <Textarea
                        name="feedback"
                        placeholder="How was your experience? Any suggestions?"
                        value={feedbackData.feedback}
                        onChange={(e) =>
                          setFeedbackData((prev) => ({
                            ...prev,
                            feedback: e.target.value,
                          }))
                        }
                        className="pl-10 min-h-24 resize-none"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className="w-full h-12 bg-red-900 hover:bg-red-800 text-white rounded-lg"
                  >
                    Complete & Submit
                  </Button>

                  <p className="text-center text-xs text-muted-foreground">
                    You'll receive a confirmation email shortly.
                  </p>
                </form>
              </div>
            )}
          </div>
        </div>

        <FooterSection />
      </main>
    </div>
  );
}
